import React, { useState } from "react";
import MedicamentRow from "./MedicamentRow";

const isMac = /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);
const ctrlLabel = isMac ? "⌃" : "Ctrl+";
let nextMedicamentKey = 1;

function SaveIcon({ size = 17 }) {
    return (
        <svg
            aria-hidden="true"
            width={size}
            height={size}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            focusable="false"
        >
            <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2Z" />
            <path d="M17 21v-8H7v8" />
            <path d="M7 3v5h8" />
        </svg>
    );
}

function createMedicamentKey() {
    return `medicament-list-${Date.now()}-${nextMedicamentKey++}`;
}

const thStyle = {
    padding: "8px 10px",
    textAlign: "left",
    fontSize: "11px",
    fontWeight: 700,
    color: "#94a3b8",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    whiteSpace: "nowrap",
    borderBottom: "1px solid #e2e8f0",
    background: "#f8fafc"
};

export default function MedicamentList({ produits, onChange, strategy, focusLastNom, onLastNomFocused, onAddProduit, commande, onSave, isDirty, validatePatientAction, productSelectionMode, selectedProductIds, onToggleProductSelection, productValidationAction }) {
    const [showDestinationErrors, setShowDestinationErrors] = useState(false);

    const handleSave = async () => {
        const result = await onSave();
        if (result === false) {
            setShowDestinationErrors(true);
        } else {
            setShowDestinationErrors(false);
        }
    };

    const showDosage = !!strategy?.showDosage;
    const showDestination = strategy?.showDestination !== false;
    const showSiBesoin = !!strategy?.showSiBesoin;
    const showClassificationMedicament = !!strategy?.showClassificationMedicament;
    const showValidationCheckbox = !!strategy?.showValidationCheckbox;
    const onlyPlanificationValidated = !!strategy?.onlyPlanificationValidated;
    // 4 fixed cols: Médicament + Posologie + Qté + Actions, plus optional cols
    const colCount = 4 + (showDosage ? 1 : 0) + (showDestination ? 1 : 0) + (showSiBesoin ? 1 : 0) + (showClassificationMedicament ? 1 : 0) + (showValidationCheckbox ? 1 : 0) + (productSelectionMode ? 1 : 0);

    const visibleProduits = onlyPlanificationValidated
        ? produits.filter(p => p.planificationValidated)
        : produits;

    const addProduit = () => {
        onChange([
            ...produits,
            {
                _clientKey: createMedicamentKey(),
                nom: "",
                codeCis: "",
                cip13: "",
                bdpmDisponibiliteStatutCodes: "",
                bdpmDisponibiliteStatuts: "",
                dosage: "",
                posologie: "",
                quantite: 1,
                destination: ""
            }
        ]);
    };

    const updateProduit = (index, fieldOrPatch, value) => {
        const copy = [...produits];
        if (typeof fieldOrPatch === 'object' && fieldOrPatch !== null) {
            copy[index] = { ...copy[index], ...fieldOrPatch };
        } else {
            copy[index] = { ...copy[index], [fieldOrPatch]: value };
        }
        onChange(copy);
    };

    const removeProduit = (index) => {
        onChange(produits.filter((_, i) => i !== index));
    };

    return (
        <div className="commande-editor-medications" style={{ borderRadius: "8px", border: "1px solid #e2e8f0", overflow: "hidden" }}>
            <table className="commande-editor-medications-table" style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                    <tr>
                        {productSelectionMode && <th style={{ ...thStyle, width: "54px", textAlign: "center" }}>Sélect.</th>}
                        <th style={{ ...thStyle, minWidth: "200px" }}>Médicament</th>
                        {showDosage && <th style={thStyle}>Dosage</th>}
                        <th style={thStyle}>Posologie</th>
                        {showSiBesoin && <th style={{ ...thStyle, textAlign: "center" }}>Si besoin</th>}
                        {showClassificationMedicament && <th style={thStyle}>Classif.</th>}
                        <th style={{ ...thStyle, textAlign: "center" }}>Qté</th>
                        {showDestination && <th style={thStyle}>Destination</th>}
                        <th style={{ ...thStyle, textAlign: "center" }}>Actions</th>
                        {showValidationCheckbox && <th style={{ ...thStyle, textAlign: "center" }}>Validé</th>}
                    </tr>
                </thead>
                <tbody>
                    {produits.length === 0 ? (
                        <tr>
                            <td
                                colSpan={colCount}
                                style={{ padding: "20px", textAlign: "center", color: "#94a3b8", fontSize: "13px" }}
                            >
                                Aucun médicament — cliquez sur « Ajouter »
                            </td>
                        </tr>
                    ) : visibleProduits.map((prod, i) => (
                        <MedicamentRow
                            key={prod._clientKey ?? `medicament-${prod.id ?? i}`}
                            prod={prod}
                            index={produits.indexOf(prod)}
                            strategy={strategy}
                            colCount={colCount}
                            onUpdate={updateProduit}
                            onRemove={removeProduit}
                            autoFocus={focusLastNom && i === visibleProduits.length - 1}
                            onFocused={onLastNomFocused}
                            commande={commande}
                            destinationError={showDestinationErrors && !prod.destination}
                            productSelectionMode={productSelectionMode}
                            selectedForValidation={prod.id != null && selectedProductIds?.has(prod.id)}
                            onToggleValidationSelection={onToggleProductSelection}
                        />
                    ))}
                </tbody>
            </table>

            <div className="commande-editor-medication-actions" style={{ borderTop: "1px solid #e2e8f0", padding: "12px 14px", background: "#f8fafc", display: "flex", gap: "8px", alignItems: "center" }}>
                {onSave && (
                    <button
                        className="commande-editor-save"
                        onClick={handleSave}
                        disabled={!isDirty}
                        title="Sauvegarder (Ctrl+S)"
                        style={{
                            padding: "10px 16px", borderRadius: "8px",
                            border: "none",
                            background: isDirty ? "#16a34a" : "#e2e8f0",
                            color: isDirty ? "white" : "#94a3b8",
                            fontSize: "14px", fontWeight: 600,
                            cursor: isDirty ? "pointer" : "not-allowed",
                            display: "inline-flex", alignItems: "center", gap: "8px",
                            transition: "background 0.15s, color 0.15s",
                        }}
                    >
                        <SaveIcon />
                        Sauvegarder
                        <kbd style={{
                            fontSize: "11px",
                            color: isDirty ? "#16a34a" : "#94a3b8",
                            background: isDirty ? "rgba(255,255,255,0.9)" : "#f1f5f9",
                            border: "none", borderRadius: "4px",
                            padding: "2px 6px", fontFamily: "inherit", fontWeight: 700,
                        }}>{ctrlLabel}S</kbd>
                    </button>
                )}
                <button
                    className="commande-editor-add-medication"
                    onClick={onAddProduit ?? addProduit}
                    style={{
                        padding: "10px 20px",
                        borderRadius: "8px",
                        border: "none",
                        background: "#0f172a",
                        color: "white",
                        fontSize: "14px",
                        fontWeight: 600,
                        cursor: "pointer",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "10px",
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = "#1e293b"}
                    onMouseLeave={e => e.currentTarget.style.background = "#0f172a"}
                >
                    <span>+ Ajouter un médicament</span>
                    <kbd style={{
                        fontSize: "11px", color: "#0f172a",
                        background: "rgba(255,255,255,0.9)", border: "none",
                        borderRadius: "4px", padding: "2px 6px", fontFamily: "inherit", fontWeight: 700,
                    }}>{ctrlLabel}M</kbd>
                </button>
                {validatePatientAction}
                {productValidationAction}
            </div>
        </div>
    );
}
